import { logger } from '@heidi/runtime/logger';
import { ConsultNoteStatus } from '@heidi/schema/ml-scribe';
import { type QueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { debounce } from 'lodash-es';

import {
  type FrontendNoteFailureReason,
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
  StreamingStatus,
} from '../../contracts';
import { getSessionDataConfig } from '../config';
import {
  markAbortAsRecoveryStream,
  markAbortAsSupersedingStream,
  type RecoveryStreamResult,
} from './generation-abort-reasons';

const DEFAULT_DEBOUNCE_TIME = 100;
const DEFAULT_DEBOUNCE_MAX_WAIT = 300;

// Named here so the export does not mention lodash's `DebouncedFunc`, which
// this package does not depend on directly.
type DebouncedConsultNoteWriter = {
  (data: string, key: string, sessionID: string, isJsonTemplate?: boolean): void;
  cancel: () => void;
  flush: () => void;
};

export const debounceSetConsultNoteQueryData: DebouncedConsultNoteWriter = debounce(
  (data: string, key: string, sessionID: string, isJsonTemplate = false) => {
    getSessionDataConfig()
      .getQueryClient()
      .setQueryData<QuerySessionResponse>([key, { sessionId: sessionID }], (oldData) => {
        if (!oldData) {
          return undefined;
        }
        if (
          oldData.streamingStatus !== StreamingStatus.STREAMING &&
          oldData.streamingStatus !== StreamingStatus.AWAITING_STREAMING_START
        ) {
          return oldData;
        }
        const newData: QuerySessionResponse = {
          ...oldData,
          consult_note: { ...oldData.consult_note, result: data },
          // if isJSONTemplate, we hide note streaming status
          streamingStatus: isJsonTemplate
            ? StreamingStatus.AWAITING_STREAMING_START
            : StreamingStatus.STREAMING,
        };
        return newData;
      });
  },
  DEFAULT_DEBOUNCE_TIME,
  { leading: true, maxWait: DEFAULT_DEBOUNCE_MAX_WAIT }
);

export const setConsultNoteQueryDataFallbackFromSideBySide = (
  data: string,
  sessionId: string
) => {
  getSessionDataConfig()
    .getQueryClient()
    .setQueryData<QuerySessionResponse>([GET_SESSION_BY_ID, { sessionId }], (oldData) => {
      if (!oldData) return undefined;
      return produce(oldData, (draft) => {
        draft.streamingStatus = StreamingStatus.STREAMING;
        draft.consult_note.result = data;
      });
    });
};

export const updateConsultNoteStreamStatus = (
  sessionId: string,
  status: StreamingStatus
) => {
  getSessionDataConfig()
    .getQueryClient()
    .setQueryData<QuerySessionResponse>([GET_SESSION_BY_ID, { sessionId }], (oldData) => {
      if (!oldData) return undefined;
      return produce(oldData, (draft) => {
        draft.streamingStatus = status;
      });
    });
};

export const getConsultNoteSettingsFromQueryCache = (sessionId: string) => {
  const cache = getSessionDataConfig()
    .getQueryClient()
    .getQueryData<QuerySessionResponse>([GET_SESSION_BY_ID, { sessionId }]);
  return {
    voice_style: cache?.consult_note.voice_style,
    brain: cache?.consult_note.brain,
    template_id: cache?.consult_note.template_id,
  };
};

export const getSessionFromCache = (
  sessionId: string
): QuerySessionResponse | undefined => {
  const key = GET_SESSION_BY_ID;
  return getSessionDataConfig()
    .getQueryClient()
    .getQueryData<QuerySessionResponse>([key, { sessionId }]);
};

/**
 * Aborts the in-flight note stream for a session as part of recovery. Marks
 * the controller first so the stream abort handler does not restore stale
 * cache state or show a stop toast.
 */
export const abortNoteStreamForRecovery = (
  sessionId: string,
  result: RecoveryStreamResult
): void => {
  const cached = getSessionFromCache(sessionId);
  const existingAbortController = cached?.abortController;
  if (existingAbortController && !existingAbortController.signal.aborted) {
    logger.debug('[HAIKU] Aborting stuck stream during recovery', { sessionId });
    markAbortAsRecoveryStream(existingAbortController, result);
    existingAbortController.abort();
  }
};

/**
 * Applies a server-fetched session into the GET_SESSION_BY_ID cache after
 * note-stream recovery polling. Aborts any in-flight SSE stream and resets
 * `streamingStatus` so Haiku UI can render the recovered note.
 */
export const applyRecoveredSessionToCache = (
  sessionId: string,
  serverSession: QuerySessionResponse
): void => {
  const key = GET_SESSION_BY_ID;
  const cached = getSessionDataConfig()
    .getQueryClient()
    .getQueryData<QuerySessionResponse>([key, { sessionId }]);

  abortNoteStreamForRecovery(
    sessionId,
    serverSession.consult_note.status === ConsultNoteStatus.COMPLETED
      ? { outcome: 'completed' }
      : {
          outcome: 'failed',
          failureReason: serverSession.consult_note.failure_reason ?? undefined,
        }
  );

  getSessionDataConfig()
    .getQueryClient()
    .setQueryData<QuerySessionResponse>([key, { sessionId }], {
      ...serverSession,
      streamingStatus: StreamingStatus.NOT_STARTED,
      chunkUploadProgress: cached?.chunkUploadProgress,
      abortController: new AbortController(),
      isDocumentFromNote:
        cached?.isDocumentFromNote ?? serverSession.isDocumentFromNote ?? false,
      isAutoGeneratedNoteWarningDismissed:
        cached?.isAutoGeneratedNoteWarningDismissed ??
        serverSession.isAutoGeneratedNoteWarningDismissed ??
        false,
      shouldShowNoteSyncChanges:
        cached?.shouldShowNoteSyncChanges ??
        serverSession.shouldShowNoteSyncChanges ??
        false,
      shouldShowDocumentSyncChanges:
        cached?.shouldShowDocumentSyncChanges ??
        serverSession.shouldShowDocumentSyncChanges ??
        false,
      isSocketError: cached?.isSocketError ?? serverSession.isSocketError ?? false,
    });
};

export const setConsultNoteAsFailed = (
  sessionId: string,
  feError?: FrontendNoteFailureReason
) => {
  getSessionDataConfig()
    .getQueryClient()
    .setQueryData<QuerySessionResponse>([GET_SESSION_BY_ID, { sessionId }], (oldData) => {
      if (!oldData) return undefined;
      return produce(oldData, (draft) => {
        draft.consult_note.status = ConsultNoteStatus.FAILED;
        draft.consult_note.should_retry = true;
        draft.streamingStatus = StreamingStatus.NOT_STARTED;
        draft.feError = feError;
      });
    });
};

/**
 * Cancels any existing stream abort controller and creates a new one for the session.
 * Stores the new abort controller in the session cache.
 *
 * @param queryClient - The React Query client instance
 * @param key - The query key (GET_SESSION_BY_ID or GET_DEMO_SESSION_BY_ID)
 * @param sessionID - The session ID
 * @returns The newly created AbortController
 */
export const cancelExistingStreamAndCreateNewAbortController = (
  queryClient: QueryClient,
  key: string,
  sessionID: string
): AbortController => {
  // Cancel any existing stream before starting a new one
  // Read the latest session cache to get the most up-to-date abort controller
  const latestSession = queryClient.getQueryData<QuerySessionResponse>([
    key,
    { sessionId: sessionID },
  ]);
  const existingAbortController = latestSession?.abortController;
  if (existingAbortController && !existingAbortController.signal.aborted) {
    logger.debug('Aborting existing stream before starting new one', {
      sessionID,
    });
    // Mark this as a supersede (not a user Stop) before aborting so the
    // outgoing stream's abort handler stays silent.
    markAbortAsSupersedingStream(existingAbortController);
    existingAbortController.abort();
  }

  // Create a new abort controller for this stream
  const ctrl = new AbortController();

  // Store the new abort controller in the session cache
  queryClient.setQueryData<QuerySessionResponse>(
    [key, { sessionId: sessionID }],
    (oldData) => {
      if (!oldData) {
        return undefined;
      }
      return {
        ...oldData,
        abortController: ctrl,
      };
    }
  );

  return ctrl;
};
